import React, { useEffect, useMemo, useRef, useState } from "react";
import { Num } from "./ui/Num";
import { FilterRuleRow } from "./FilterRuleRow";
import { useFilterPreview } from "./useBroadcastFilters";
import { MIN_FILTER_RULES } from "./broadcastEnums";

/**
 * The create/edit modal for ONE saved audience filter.
 *
 * ---------------------------------------------------------------------------
 * AN EMPTY RULE LIST IS NOT "EVERYONE"
 * ---------------------------------------------------------------------------
 * This is the rule the whole component is arranged around. `all` is how you
 * address everyone — it is a segment, it is one click, and it is already in the
 * composer. An empty rule list means something else entirely: an author has
 * opened the builder and not typed anything yet. The server agrees, and answers
 * `POST /filters/preview` with a 422 for zero rules.
 *
 * So below `MIN_FILTER_RULES` this screen shows a PROMPT, never a count. Not
 * "0 people match" — which is a finding, and one an author acts on by rewriting
 * rules that were never written — and not a blank space either. Save is
 * disabled for the same reason: a filter with no rules cannot be saved, and
 * finding that out from a red 422 after clicking is finding it out too late.
 *
 * ---------------------------------------------------------------------------
 * ALL RULES ARE ANDed
 * ---------------------------------------------------------------------------
 * A stack of rows with nothing between them reads as OR to plenty of people,
 * and an OR misread as an AND is the difference between messaging forty people
 * and messaging four thousand. The word "and" is therefore RENDERED between
 * rows as text, not implied by layout.
 *
 * ---------------------------------------------------------------------------
 * TWO REACHABILITY NUMBERS FROM ONE FILTER IS CORRECT
 * ---------------------------------------------------------------------------
 * A filter answers WHO. Each channel then applies its own reachability — a live
 * device token for push, a deliverable address that is not suppressed for
 * email — so the same filter yields two different numbers. Both are rendered,
 * both are labelled, and the screen says why in one line, because the
 * alternative is a support ticket asking which of the two is the bug.
 *
 * ---------------------------------------------------------------------------
 * `refreshedAt` IS NOT DECORATION
 * ---------------------------------------------------------------------------
 * These counts come from a nightly rollup. A number with no timestamp claims to
 * be live, and a stale number pretending to be live is worse than a stale
 * number that says so. When the server sends no instant we render "as of
 * unknown" rather than dropping the line — an unqualified number is exactly the
 * claim we are trying not to make.
 */

const TITLE_ID = "bc-filter-builder-title";

const BUTTON_BASE =
  "inline-flex items-center justify-center rounded-lg px-4 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2";

const PRIMARY_BUTTON = "bg-orange-primary text-white hover:bg-[#E85A10]";

const GHOST_BUTTON = "text-bc-text-2 hover:bg-bc-subtle";

const DISABLED_BUTTON = "cursor-not-allowed opacity-50";

const INPUT =
  "w-full rounded-lg border border-bc-border bg-white px-2.5 py-1.5 text-[13px] text-bc-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

/** A blank row. `op` and `value` stay empty until a field decides their shape. */
const EMPTY_RULE = { field: "", op: "", value: null };

/**
 * Is this rule complete enough to be worth previewing?
 *
 * An incomplete row is a 422 waiting to happen, and a red line under a form
 * nobody has finished is noise. The row still counts towards `rules.length` —
 * the author added it, so the prompt is gone and Save is a real question — it
 * simply is not sent until it says something.
 *
 * @param {{field: string, op: string, value: *}} rule
 * @returns {boolean}
 */
const isComplete = (rule) =>
  Boolean(rule?.field) &&
  Boolean(rule?.op) &&
  rule?.value !== null &&
  rule?.value !== undefined &&
  rule?.value !== "";

/**
 * "as of 4 Aug, 09:30", or "as of unknown".
 *
 * Never returns the empty string: the caller renders this unconditionally, and
 * the whole point is that a count is never shown without a vintage.
 *
 * @param {string|null|undefined} refreshedAt - The raw instant from the server.
 * @returns {string}
 */
export const asOfText = (refreshedAt) => {
  if (!refreshedAt) return "as of unknown";
  const when = new Date(refreshedAt);
  // `new Date("soon").toLocaleString()` is the literal string "Invalid Date".
  if (Number.isNaN(when.getTime())) return "as of unknown";
  return `as of ${when.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/* ── The count block ──────────────────────────────────────────────────── */

/**
 * The live count, or the prompt that replaces it.
 *
 * @param {Object} props
 * @param {number} props.ruleCount - How many rows the author has added.
 * @param {Object|null} props.preview - Decoded preview, or null.
 * @param {boolean} props.previewing - A preview request is in flight.
 * @param {string|null} props.previewError - Copy for a failed preview.
 * @returns {React.ReactElement}
 */
const CountBlock = ({ ruleCount, preview, previewing, previewError }) => {
  /*
   * THE HEADLINE. Below the minimum there is no count region at all — not a
   * zero, not an em dash, not a spinner. Nothing numeric renders here, and the
   * test asserts the absence character by character.
   */
  if (ruleCount < MIN_FILTER_RULES) {
    return (
      <div
        data-testid="bc-filter-count"
        className="mt-4 rounded-lg border border-dashed border-bc-border p-4 text-[13px] text-bc-soft"
      >
        Add a rule to see how many people match.
      </div>
    );
  }

  return (
    <div
      data-testid="bc-filter-count"
      className="mt-4 rounded-lg border border-bc-border bg-bc-subtle p-4"
    >
      <div className="flex items-baseline gap-2">
        <Num value={preview?.matched} className="text-2xl" emphasis />
        <span className="text-[13px] text-bc-text-2">people match</span>
        <span data-testid="bc-filter-as-of" className="text-xs text-bc-faint">
          {asOfText(preview?.refreshedAt)}
        </span>
      </div>

      <div className="mt-2 flex flex-col gap-0.5 text-xs text-bc-muted">
        <span data-testid="bc-filter-push-reach">
          <Num value={preview?.pushReachable} className="text-xs" /> can receive
          a push
        </span>
        <span data-testid="bc-filter-email-reach">
          <Num value={preview?.emailReachable} className="text-xs" /> can receive
          an email
        </span>
      </div>

      {/* One filter, two numbers — said out loud, before anyone files it. */}
      <p className="mt-2 text-xs text-bc-soft">
        One filter serves both channels: it decides who, then each channel
        applies its own reachability, so these two numbers differ.
      </p>

      {previewing ? (
        <p className="mt-2 text-xs text-bc-faint">Counting…</p>
      ) : null}

      {/* A failed preview is "we could not check", never a zero. `Num` already
          renders the em dash above; this says why it is there. */}
      {previewError ? (
        <p className="mt-2 text-xs text-bc-warn">{previewError}</p>
      ) : null}
    </div>
  );
};

/* ── The modal ────────────────────────────────────────────────────────── */

/**
 * Create or edit one saved filter.
 *
 * @param {Object} props
 * @param {boolean} props.open - Renders nothing when false.
 * @param {{id?: string, name?: string, rules?: Array<Object>}|null} [props.filter] - The filter being edited, or null/absent to create one.
 * @param {Array<Object>} props.fields - Decoded field registry, passed straight to each row.
 * @param {(body: {name: string, rules: Array<Object>}) => void} [props.onSave] - Called with the camelCase body. Never a wire shape.
 * @param {() => void} [props.onCancel] - Called by Cancel, Escape and an overlay click.
 * @param {boolean} [props.saving=false] - A save is in flight; both buttons disable.
 * @param {string|null} [props.error] - Copy for a failed save. Rendered with role="alert".
 * @returns {React.ReactElement|null}
 */
export const FilterBuilder = ({
  open,
  filter,
  fields,
  onSave,
  onCancel,
  saving = false,
  error = null,
}) => {
  const dialogRef = useRef(null);
  const [name, setName] = useState("");
  const [rules, setRules] = useState([]);

  // Seed from the filter each time the modal opens. Keyed on `open` as well as
  // the filter so re-opening after a cancel does not resurrect the abandoned
  // edit — the author cancelled it, and it should be gone.
  useEffect(() => {
    if (!open) return;
    setName(filter?.name || "");
    setRules(Array.isArray(filter?.rules) && filter.rules.length > 0
      ? filter.rules.map((rule) => ({ ...rule }))
      : []);
  }, [open, filter]);

  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  // Only complete rows are previewed; see `isComplete`. Memoised so the array
  // identity only moves when its contents do — the hook keys on a
  // serialization, but there is no reason to hand it fresh garbage per render.
  const previewRules = useMemo(() => rules.filter(isComplete), [rules]);
  const {
    preview,
    previewing,
    error: previewError,
  } = useFilterPreview(previewRules);

  if (!open) return null;

  // Gated on COMPLETE rules, not row count. A half-filled row — a field with
  // no operator, an operator with no value — is a 422 the moment it is saved,
  // and the author has no way to connect that rejection to the row that
  // caused it. `previewRules` already applies the same test, so what you can
  // save is exactly what the count above it describes.
  const completeCount = previewRules.length;
  const hasIncompleteRule = rules.length > completeCount;
  // Every row must be finished, not just enough of them. `handleSave` sends
  // `rules` whole, so one unfinished row alongside a good one would travel
  // with it and 422 the entire filter.
  const canSave =
    completeCount >= MIN_FILTER_RULES && !hasIncompleteRule && !saving;

  const patchRule = (index, next) =>
    setRules((current) => current.map((rule, at) => (at === index ? next : rule)));

  const removeRule = (index) =>
    setRules((current) => current.filter((_, at) => at !== index));

  const addRule = () => setRules((current) => [...current, { ...EMPTY_RULE }]);

  const handleOverlayClick = (event) => {
    // Backdrop only. A drag that starts inside the dialog and ends on its edge
    // must not throw away the rules it was editing.
    if (event.target === event.currentTarget) onCancel?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") onCancel?.();
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave?.({ name: name.trim(), rules });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        className="max-h-full w-full max-w-2xl overflow-y-auto rounded-[10px] border border-bc-border bg-white p-5 focus-visible:outline-none"
      >
        <h2 id={TITLE_ID} className="text-base font-semibold text-bc-text">
          {filter?.id ? "Edit filter" : "New filter"}
        </h2>

        <label className="mt-4 block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bc-faint">
            Name
          </span>
          <input
            type="text"
            aria-label="Filter name"
            className={`${INPUT} mt-1`}
            value={name}
            placeholder="Expiring elite members"
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <div className="mt-4 flex flex-col gap-2">
          {rules.map((rule, index) => (
            <React.Fragment key={`rule-${index}`}>
              {/* ANDed, said in words. See the header. */}
              {index > 0 ? (
                <div
                  data-testid={`bc-rule-conjunction-${index}`}
                  className="pl-1 text-xs font-semibold uppercase tracking-[0.06em] text-bc-faint"
                >
                  and
                </div>
              ) : null}
              <FilterRuleRow
                rule={rule}
                fields={fields}
                index={index}
                onChange={(next) => patchRule(index, next)}
                onRemove={() => removeRule(index)}
              />
            </React.Fragment>
          ))}
        </div>

        <button
          type="button"
          onClick={addRule}
          className={`${BUTTON_BASE} ${GHOST_BUTTON} mt-2 px-2`}
        >
          Add rule
        </button>

        <CountBlock
          ruleCount={rules.length}
          preview={preview}
          previewing={previewing}
          previewError={previewError}
        />

        {error ? (
          <div role="alert" className="mt-3 text-xs text-bc-bad">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className={`${BUTTON_BASE} ${GHOST_BUTTON} ${saving ? DISABLED_BUTTON : ""}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            title={
              hasIncompleteRule && !saving
                ? "Finish every rule before saving."
                : undefined
            }
            className={`${BUTTON_BASE} ${PRIMARY_BUTTON} ${canSave ? "" : DISABLED_BUTTON}`}
          >
            {saving ? "Saving…" : "Save filter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBuilder;
